import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  TextField,
  Button,
  Box,
  Alert,
  CircularProgress,
  ToggleButtonGroup,
  ToggleButton,
} from '@mui/material';
import { CheckCircle as CheckCircleIcon } from '@mui/icons-material';
import api from '../../utils/api';
import { FeedbackFormData, FeedbackReason, FEEDBACK_REASON_LABELS } from '../../types/applicationFeedback.types';

const ApplicationFeedback: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [alreadySubmitted, setAlreadySubmitted] = useState(false);
  const [counsellorName, setCounsellorName] = useState('');
  const [redirectCountdown, setRedirectCountdown] = useState(3);
  
  const [formData, setFormData] = useState<FeedbackFormData>({
    reason: '',
    reasonText: '',
    wouldReapplyLater: false,
    suggestions: ''
  });

  useEffect(() => {
    const loadFeedbackForm = async () => {
      try {
        const response = await api.get(`/api/application-feedback/form/${token}`);
        setCounsellorName(response.data.data.counsellorName);
        setLoading(false);
      } catch (err: any) {
        const errorMessage = err.response?.data?.error || 'Failed to load feedback form';
        // Check if feedback was already submitted
        if (errorMessage.includes('already been submitted')) {
          setAlreadySubmitted(true);
        }
        setError(errorMessage);
        setLoading(false);
      }
    };

    if (token) {
      loadFeedbackForm();
    }
  }, [token]);

  // Redirect countdown after successful submission
  useEffect(() => {
    if (submitted && redirectCountdown > 0) {
      const timer = setTimeout(() => {
        setRedirectCountdown(redirectCountdown - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else if (submitted && redirectCountdown === 0) {
      window.location.href = 'https://evolving-minds.org.uk';
    }
  }, [submitted, redirectCountdown]);

  const handleReasonChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      reason: event.target.value as FeedbackReason
    });
  };

  const handleWouldReapplyChange = (
    event: React.MouseEvent<HTMLElement>,
    newValue: boolean | null
  ) => {
    if (newValue !== null) {
      setFormData({
        ...formData,
        wouldReapplyLater: newValue
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.reason) {
      setError('Please select a reason');
      return;
    }

    try {
      setSubmitting(true);
      setError(null);
      
      await api.post(`/api/application-feedback/submit/${token}`, formData);
      
      setSubmitted(true);
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || 'Failed to submit feedback';
      // Check if feedback was already submitted
      if (errorMessage.includes('already been submitted')) {
        setAlreadySubmitted(true);
      }
      setError(errorMessage);
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ mt: 8, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (alreadySubmitted) {
    return (
      <Container maxWidth="md" sx={{ mt: 8 }}>
        <Paper elevation={3} sx={{ p: 4, textAlign: 'center' }}>
          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Feedback Already Submitted
            </Typography>
            <Typography variant="body2">
              You have already submitted your feedback for this application. Thank you for your response!
            </Typography>
          </Alert>
          <Button
            variant="contained"
            onClick={() => window.location.href = 'https://evolving-minds.org.uk'}
            sx={{ mt: 2 }}
          >
            Visit Evolving Minds
          </Button>
        </Paper>
      </Container>
    );
  }

  if (submitted) {
    return (
      <Container maxWidth="md" sx={{ mt: 8 }}>
        <Paper elevation={3} sx={{ p: 4, textAlign: 'center' }}>
          <CheckCircleIcon sx={{ fontSize: 80, color: 'success.main', mb: 2 }} />
          <Typography variant="h4" gutterBottom>
            Thank You!
          </Typography>
          <Typography variant="body1" color="text.secondary" paragraph>
            Your feedback has been submitted successfully.
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            We appreciate you taking the time to help us improve our recruitment process.
          </Typography>
          <Typography variant="body2" color="primary" sx={{ mt: 3, fontWeight: 'bold' }}>
            Redirecting to Evolving Minds in {redirectCountdown} second{redirectCountdown !== 1 ? 's' : ''}...
          </Typography>
          <Button
            variant="outlined"
            onClick={() => window.location.href = 'https://evolving-minds.org.uk'}
            sx={{ mt: 2 }}
          >
            Go Now
          </Button>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ mt: 8, mb: 8 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Box sx={{ mb: 4, textAlign: 'center' }}>
          <Typography variant="h4" gutterBottom>
            Application Feedback
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Hi {counsellorName}, we'd love to hear why you didn't complete your application
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          <FormControl component="fieldset" fullWidth sx={{ mb: 4 }}>
            <FormLabel component="legend" sx={{ mb: 2, fontWeight: 'bold' }}>
              What was the main reason you didn't complete the application? *
            </FormLabel>
            <RadioGroup value={formData.reason} onChange={handleReasonChange}>
              {(Object.entries(FEEDBACK_REASON_LABELS) as [FeedbackReason, string][]).map(([value, label]) => (
                <FormControlLabel
                  key={value}
                  value={value}
                  control={<Radio />}
                  label={label}
                  sx={{ mb: 1 }}
                />
              ))}
            </RadioGroup>
          </FormControl>

          {formData.reason === 'OTHER' && (
            <TextField
              fullWidth
              label="Please specify"
              multiline
              rows={3}
              value={formData.reasonText}
              onChange={(e) => setFormData({ ...formData, reasonText: e.target.value })}
              sx={{ mb: 4 }}
              required
            />
          )}

          {formData.reason && formData.reason !== 'OTHER' && (
            <TextField
              fullWidth
              label="Additional details (optional)"
              multiline
              rows={3}
              value={formData.reasonText}
              onChange={(e) => setFormData({ ...formData, reasonText: e.target.value })}
              sx={{ mb: 4 }}
            />
          )}

          <FormControl component="fieldset" fullWidth sx={{ mb: 4 }}>
            <FormLabel component="legend" sx={{ mb: 2, fontWeight: 'bold' }}>
              Would you consider applying again in the future? *
            </FormLabel>
            <ToggleButtonGroup
              value={formData.wouldReapplyLater}
              exclusive
              onChange={handleWouldReapplyChange}
              aria-label="would reapply"
              fullWidth
            >
              <ToggleButton value={true} aria-label="yes">
                Yes
              </ToggleButton>
              <ToggleButton value={false} aria-label="no">
                No
              </ToggleButton>
            </ToggleButtonGroup>
          </FormControl>

          <TextField
            fullWidth
            label="Do you have any suggestions for improving our application process? (optional)"
            multiline
            rows={4}
            value={formData.suggestions}
            onChange={(e) => setFormData({ ...formData, suggestions: e.target.value })}
            sx={{ mb: 4 }}
            placeholder="Your suggestions help us make the process better for future applicants..."
          />

          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
            <Button
              type="submit"
              variant="contained"
              size="large"
              disabled={submitting || !formData.reason}
              sx={{ minWidth: 200 }}
            >
              {submitting ? 'Submitting...' : 'Submit Feedback'}
            </Button>
          </Box>
        </form>

        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 4, textAlign: 'center' }}>
          Your responses are confidential and will only be used to improve our recruitment process.
        </Typography>
      </Paper>
    </Container>
  );
};

export default ApplicationFeedback;
